import {
	ReportBlockData,
	ReportBlockRender,
	ReportBlockSeries,
	ReportBlockShape
} from './report-blocks.service';
import { blockEntityNames, blockShape } from './report-block-chart-config';

/**
 * What a chart in a report can be switched to, and what reads well.
 *
 * Every chart — an added one and the chart inside each fixed section — can be
 * redrawn as any type its numbers can honestly take, in one click. The list is
 * decided by the SHAPE of the numbers, the same rule the backend holds
 * (`SHAPE_RENDERS` in `reports/blocks/catalogue.py`, and `SECTION_CHART_SHAPES`
 * in `reports/schema.py` for the sections), because a type offered here that
 * the backend refuses would make the report unsaveable. Keep the two lists
 * entry for entry.
 *
 * A type that reads the numbers badly is still offered: the reader asked for
 * it, and a pie of thirty days is still those thirty days. It is flagged with
 * the reason instead (`chartTypeAdvice`), and never refused.
 */

/** One entry of the type menu. */
export interface ReportChartTypeOption {
	render: ReportBlockRender;
	label: string;
}

/** The menu, in the order it is offered. */
export const REPORT_CHART_TYPE_OPTIONS: readonly ReportChartTypeOption[] = [
	{ render: 'line', label: 'Line' },
	{ render: 'area', label: 'Area' },
	{ render: 'bar', label: 'Bars' },
	{ render: 'stacked_bar', label: 'Stacked bars' },
	{ render: 'ranked_bar', label: 'Ranked bars' },
	{ render: 'doughnut', label: 'Donut' },
	{ render: 'pie', label: 'Pie' },
	{ render: 'polarArea', label: 'Polar area' },
	{ render: 'table', label: 'Table' },
	{ render: 'kpi', label: 'Stat cards' },
	{ render: 'matrix', label: 'Heatmap' },
	{ render: 'wordCloud', label: 'Word cloud' },
	{ render: 'timeline', label: 'Timeline' },
	{ render: 'rating_bars', label: 'Rating bars' }
];

/** Everything each shape can honestly be drawn as — the backend's list. */
export const REPORT_SHAPE_RENDERS: Readonly<
	Record<ReportBlockShape, readonly ReportBlockRender[]>
> = {
	series: [
		'line',
		'area',
		'bar',
		'stacked_bar',
		'ranked_bar',
		'pie',
		'doughnut',
		'polarArea',
		'table',
		'kpi'
	],
	categories: [
		'bar',
		'ranked_bar',
		'stacked_bar',
		'doughnut',
		'pie',
		'polarArea',
		'wordCloud',
		'line',
		'area',
		'table',
		'kpi'
	],
	matrix: ['matrix', 'line', 'bar', 'table'],
	rows: ['table', 'ranked_bar', 'bar'],
	creators: ['creators'],
	kpi: ['kpi', 'table'],
	mention: ['quote'],
	mentions: ['quote'],
	events: ['table', 'timeline'],
	ratings: ['rating_bars', 'table', 'ranked_bar', 'bar']
};

/** A fixed section's chart: what its numbers are, and what it is designed as. */
export interface ReportSectionChart {
	shape: ReportBlockShape;
	/**
	 * The type the section's own slide draws. Picking it again puts the
	 * section's own design back, which is what a stored `chart_type` of null
	 * means — so a report never stores the default as a choice.
	 */
	designedAs: ReportBlockRender;
	/** How many series it draws, when more than one — what a pie folds away. */
	series?: number;
}

/**
 * The fixed sections whose visual is a chart. Executive Summary is stat cards
 * already, and Audience Demographics draws a placeholder ring until its data
 * exists, so neither has anything to switch.
 */
export const REPORT_SECTION_CHARTS: Readonly<
	Record<string, ReportSectionChart>
> = {
	volume_of_mentions_reach: { shape: 'series', designedAs: 'area' },
	sentiment_over_time: { shape: 'series', designedAs: 'line', series: 3 },
	emotion_over_time: { shape: 'series', designedAs: 'area', series: 6 },
	sentiment_by_media_type: {
		shape: 'categories',
		designedAs: 'bar',
		series: 3
	},
	emotion_analysis: { shape: 'categories', designedAs: 'polarArea' },
	top_influencers_by_voice_share: {
		shape: 'categories',
		designedAs: 'ranked_bar'
	},
	top_sites_by_visits: { shape: 'categories', designedAs: 'ranked_bar' },
	influencers_by_media_type: { shape: 'categories', designedAs: 'pie' },
	best_time_to_post: { shape: 'matrix', designedAs: 'matrix' },
	top_countries: { shape: 'categories', designedAs: 'ranked_bar' },
	trending_hashtags: { shape: 'categories', designedAs: 'ranked_bar' },
	trending_conversations: { shape: 'categories', designedAs: 'ranked_bar' },
	mentions_by_media_type: { shape: 'categories', designedAs: 'doughnut' },
	languages: { shape: 'categories', designedAs: 'doughnut' }
};

/** A reader switched a fixed section's chart: which one, and to what. */
export interface ReportSectionChartTypeChange {
	sectionId: string;
	/** Null puts the section's own design back. */
	chartType: ReportBlockRender | null;
}

/** Every type a fixed section's chart may be drawn as; empty when it has none. */
export function sectionChartTypes(sectionId: string): ReportBlockRender[] {
	const chart = REPORT_SECTION_CHARTS[sectionId];
	return chart ? [...REPORT_SHAPE_RENDERS[chart.shape]] : [];
}

const SLICED: readonly ReportBlockRender[] = ['pie', 'doughnut', 'polarArea'];

/** More slices than this and a pie stops being comparable at a glance. */
const MAX_SLICES = 8;
/** More rows than this do not fit one slide's chart area. */
const MAX_TABLE_ROWS = 15;
const MAX_CARDS = 12;

/** What advice needs to know about a chart's numbers — their outline. */
interface ChartOutline {
	shape: ReportBlockShape;
	series: number;
	labels: number;
	/** Rows a table of it would have; 0 when not known. */
	tableRows: number;
	comparing: boolean;
}

function outlineOf(block: ReportBlockData): ChartOutline {
	const shape = blockShape(block);
	return {
		shape,
		series: block.series?.length ?? 0,
		labels: block.labels?.length ?? 0,
		tableRows:
			shape === 'matrix'
				? 7
				: shape === 'kpi'
				? Object.keys(block.figures ?? {}).length
				: block.rows?.length || block.labels?.length || 0,
		comparing: blockEntityNames(block).length > 1
	};
}

/**
 * Why `render` reads these numbers badly, or null when it reads fine.
 *
 * Advice, never a gate: the menu still offers the type and the switch still
 * happens. Short, because it is read in a tooltip and relayed by Ana.
 */
function adviceFor(
	outline: ChartOutline,
	render: ReportBlockRender
): string | null {
	const { shape, series, labels } = outline;
	const sliced = SLICED.includes(render);

	if ((sliced || render === 'wordCloud') && outline.comparing) {
		return 'two brands cannot share one ring, so it is drawn as bars';
	}
	if (shape === 'series') {
		if (sliced) {
			return series > 1
				? 'a pie keeps each total but loses how it moved'
				: 'a slice per day hides the trend';
		}
		if (render === 'ranked_bar') {
			return 'ranking the days breaks the timeline';
		}
	}
	if (shape === 'categories') {
		if (render === 'line' || render === 'area') {
			return 'a line implies an order these categories do not have';
		}
		if (sliced && series > 1) {
			return 'a pie folds the breakdown into one total per series';
		}
		if (sliced && labels > MAX_SLICES) {
			return 'too many slices to compare';
		}
		if (render === 'kpi' && series === 1 && labels > MAX_CARDS) {
			return 'too many cards to compare';
		}
	}
	if (shape === 'matrix' && (render === 'line' || render === 'bar')) {
		return 'seven weekdays on one chart are hard to tell apart';
	}
	if (render === 'table' && outline.tableRows > MAX_TABLE_ROWS) {
		return 'too many rows for one slide';
	}
	return null;
}

/** Why `render` reads this block's numbers badly, or null. */
export function chartTypeAdvice(
	block: ReportBlockData,
	render: ReportBlockRender
): string | null {
	return adviceFor(outlineOf(block), render);
}

/**
 * The same advice for a fixed section before its numbers are at hand — what
 * Ana is told when she switches one. It knows the section's shape and how
 * many series it draws, not how many days or rows, so it says only what
 * follows from those.
 */
export function sectionChartTypeAdvice(
	sectionId: string,
	render: ReportBlockRender
): string | null {
	const chart = REPORT_SECTION_CHARTS[sectionId];
	return chart
		? adviceFor(
				{
					shape: chart.shape,
					series: chart.series ?? 1,
					labels: 0,
					tableRows: 0,
					comparing: false
				},
				render
		  )
		: null;
}

/** The advice for every type offered, keyed by type; absent where it reads fine. */
export function chartTypeAdviceFor(
	block: ReportBlockData | null,
	renders: readonly ReportBlockRender[]
): Partial<Record<ReportBlockRender, string>> {
	const advice: Partial<Record<ReportBlockRender, string>> = {};
	if (!block) {
		return advice;
	}
	for (const render of renders) {
		const reason = chartTypeAdvice(block, render);
		if (reason) {
			advice[render] = reason;
		}
	}
	return advice;
}

/** What a fixed section hands over of its chart: the numbers it draws. */
export interface ReportSectionChartNumbers {
	labels?: string[];
	series?: ReportBlockSeries[];
	/** For a heatmap: one row per filled cell, `day` / `hour` / `value`. */
	rows?: Array<Record<string, string | number>>;
	labelColors?: string[];
	labelColumn?: string;
	seriesLegendShown?: boolean;
}

/**
 * A fixed section's numbers in the form every chart type is drawn from — the
 * same form an added chart arrives in, so one renderer draws both.
 *
 * Built by each slide from the data it already fetched, never from a second
 * read: switching a section's type redraws what is on the page.
 */
export function sectionChartSource(
	sectionId: string,
	numbers: ReportSectionChartNumbers
): ReportBlockData | null {
	const chart = REPORT_SECTION_CHARTS[sectionId];
	if (!chart) {
		return null;
	}
	const isMatrix = chart.shape === 'matrix';
	return {
		block_id: sectionId,
		render: chart.designedAs,
		title: '',
		shape: chart.shape,
		renders: sectionChartTypes(sectionId),
		labels: numbers.labels ?? [],
		series: numbers.series ?? [],
		columns: isMatrix
			? [
					{ key: 'day', label: 'Day', kind: 'text' },
					{ key: 'hour', label: 'Hour', kind: 'number' },
					{ key: 'value', label: 'Value', kind: 'number' }
			  ]
			: [],
		rows: numbers.rows ?? [],
		figures: {},
		total: null,
		empty: false,
		note: null,
		labelColors: numbers.labelColors,
		labelColumn: numbers.labelColumn,
		seriesLegendShown: numbers.seriesLegendShown
	};
}

/** A fixed section's chart as the slide draws it right now. */
export interface ReportSectionChartSwitch {
	/** Every type offered; empty for a section with no chart. */
	types: ReportBlockRender[];
	/** The type drawn now — the section's own design until one is picked. */
	current: ReportBlockRender;
	designedAs: ReportBlockRender;
	/**
	 * The section's numbers to draw as `current`, or null while the slide
	 * draws its own design — which is every time `current` is `designedAs`.
	 */
	block: ReportBlockData | null;
	advice: Partial<Record<ReportBlockRender, string>>;
	/**
	 * The section's numbers whatever they are drawn as — what the figures a
	 * text quotes are read from (report-quoted-figures.ts). Null while the
	 * slide has none.
	 */
	source: ReportBlockData | null;
}

/**
 * Work out what a fixed section's chart is drawn as.
 *
 * `chartType` is the stored choice; a value the section cannot take — one
 * written by hand, or by an older build — falls back to the section's own
 * design rather than to a chart with nothing in it. `source` is the section's
 * numbers in the one form every type is drawn from, with `render` left for
 * this to fill in.
 */
export function sectionChartSwitch(
	sectionId: string,
	chartType: ReportBlockRender | null | undefined,
	source: ReportBlockData | null
): ReportSectionChartSwitch {
	const chart = REPORT_SECTION_CHARTS[sectionId];
	const types = sectionChartTypes(sectionId);
	const designedAs = chart?.designedAs ?? 'bar';
	const current =
		chartType && types.includes(chartType) ? chartType : designedAs;
	return {
		types,
		current,
		designedAs,
		block:
			source && current !== designedAs
				? { ...source, render: current }
				: null,
		advice: chartTypeAdviceFor(source, types),
		source
	};
}
